import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { apiService, graphBuilderApi } from '../../services/api';
import {
  EvidenceTimeline,
  ReasoningMap,
  CitationPanel,
  IntegrityBadge,
  StabilityCard,
  WhatIfPanel,
  DerivedRuleCard,
  ActivationGraph,
} from '../explainability';
import TextWithTables from '../common/TextWithTables';
import { renderTextWithCitations } from '../../utils/citationFormatter';
import BuildGraphMenu from './BuildGraphMenu';
import './BuildGraph.css';
import './ExplainabilityPage.css';

const PAGE_SIZE = 50;

function ExplainabilityPage() {
  const { graphId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [insights, setInsights] = useState([]);
  const [insightsTotal, setInsightsTotal] = useState(0);
  const [selectedInsightId, setSelectedInsightId] = useState(searchParams.get('insight') || '');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [contributions, setContributions] = useState(null);
  const [activationTrace, setActivationTrace] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [domain, setDomain] = useState('general');
  const [availableDomains, setAvailableDomains] = useState([]);
  const [answerView, setAnswerView] = useState('citations'); // 'citations' | 'plain'
  const [highlightedCite, setHighlightedCite] = useState(null);
  const [showDetailed, setShowDetailed] = useState(false);
  const [error, setError] = useState(null);

  const answerRef = useRef(null);

  /* ── Load insights list ─────────────────────────────────────────── */

  const loadInsights = useCallback(async (pg = 1, append = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.listExecutionInsights(graphId, pg, PAGE_SIZE);
      if (res?.success) {
        setInsights((prev) => append ? [...prev, ...(res.insights || [])] : (res.insights || []));
        setInsightsTotal(res.total_count || 0);
      }
    } catch (err) {
      console.error('Failed to load insights:', err);
      setError('Could not load past evaluations. Try refreshing.');
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
    loadInsights(1, false);
    setPage(1);
    // Domains are static; load once.
    graphBuilderApi
      .getAvailableDomains()
      .then((d) => Array.isArray(d) && setAvailableDomains(d))
      .catch(() => setAvailableDomains(['general', 'aml', 'healthcare', 'insurance']));
  }, [graphId, loadInsights]);

  // Browser back/forward: keep selectedInsightId in sync with the URL.
  useEffect(() => {
    const urlInsight = searchParams.get('insight') || '';
    if (urlInsight !== selectedInsightId) {
      setSelectedInsightId(urlInsight);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    loadInsights(next, true);
  };

  const handleRefresh = () => {
    setPage(1);
    loadInsights(1, false);
  };

  /* ── Load explanation for selected insight ───────────────────────── */

  const loadExplanation = useCallback(async (insightId) => {
    if (!insightId) return;
    setDataLoading(true);
    setExplanation(null);
    setContributions(null);
    setHighlightedCite(null);
    try {
      const data = await graphBuilderApi.getBusinessExplanation({
        insight_id: insightId,
        domain,
        include_narrative: true,
      });
      setExplanation(data);
    } catch (err) {
      console.error('Failed to load business explanation:', err);
      setError('Could not load this evaluation\'s explanation.');
    } finally {
      setDataLoading(false);
    }
  }, [domain]);

  // Sync selectedInsightId <-> ?insight= URL param.
  useEffect(() => {
    if (!selectedInsightId) return;
    if (searchParams.get('insight') !== selectedInsightId) {
      const next = new URLSearchParams(searchParams);
      next.set('insight', selectedInsightId);
      setSearchParams(next, { replace: true });
    }
    loadExplanation(selectedInsightId);
  }, [selectedInsightId, loadExplanation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load causal contributions when the user opens the Reasoning Map tab.
  const loadContributions = useCallback(async () => {
    if (!selectedInsightId || contributions) return;
    try {
      const data = await graphBuilderApi.getContributions(selectedInsightId, domain);
      setContributions(data);
    } catch (err) {
      console.error('Failed to load contributions:', err);
    }
  }, [selectedInsightId, domain, contributions]);

  const loadActivationTrace = useCallback(async () => {
    if (!selectedInsightId || activationTrace) return;
    try {
      const data = await apiService.getExecutionInsight(selectedInsightId);
      if (data?.success && data?.insight?.execution_trace) {
        setActivationTrace(data.insight.execution_trace);
      }
    } catch (err) {
      console.error('Failed to load activation trace:', err);
    }
  }, [selectedInsightId, activationTrace]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'reasoning') loadContributions();
    if (tab === 'activation') loadActivationTrace();
  };

  /* ── Citation interaction ────────────────────────────────────────── */

  const handleCiteClick = useCallback((eventOrNum, citeEntry) => {
    // renderTextWithCitations passes (event, citeEntry); CitationPanel passes (cite_num).
    const num = typeof eventOrNum === 'number'
      ? eventOrNum
      : (citeEntry?.cite_num ?? null);
    if (num == null) return;
    setHighlightedCite(num);
    if (answerRef.current) {
      const sup = answerRef.current.querySelector(`[data-cite-num="${num}"]`);
      if (sup) sup.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  /* ── Derived data ────────────────────────────────────────────────── */

  const selectedInsight = useMemo(
    () => insights.find((i) => i.insight_id === selectedInsightId) || null,
    [insights, selectedInsightId],
  );
  const queryRunId =
    explanation?.metadata?.query_run_id ||
    selectedInsight?.metadata?.query_run_id ||
    selectedInsight?.answer_metadata?.query_run_id ||
    null;
  const citationIndex = explanation?.citation_index || [];
  const evidenceFactors = explanation?.evidence_factors || [];
  const usedFactors = useMemo(
    () => evidenceFactors.filter((f) => f.used_in_answer),
    [evidenceFactors],
  );
  const consideredFactors = useMemo(
    () => evidenceFactors.filter((f) => !f.used_in_answer),
    [evidenceFactors],
  );
  const decisionRules = explanation?.decision_rules_evaluated || [];

  /* ── Answer rendering ────────────────────────────────────────────── */

  const renderAnswer = () => {
    if (!explanation) return null;
    if (answerView === 'plain') {
      const text = explanation.narrative_text
        || explanation.narrative?.executive_summary
        || explanation.decision_rationale
        || explanation.answer_text
        || '';
      return (
        <TextWithTables
          text={text}
          className="explain-answer-text"
          preformatted={false}
        />
      );
    }
    const text = explanation.answer_text || explanation.decision_rationale || '';
    if (!text) {
      return <p className="explain-answer-empty">No answer text was recorded for this evaluation.</p>;
    }
    if (citationIndex.length) {
      return (
        <TextWithTables
          text={text}
          className="explain-answer-text"
          preformatted={false}
          renderText={(t, key) => renderTextWithCitations(t, citationIndex, handleCiteClick, key)}
        />
      );
    }
    return <TextWithTables text={text} className="explain-answer-text" preformatted={false} />;
  };

  /* ── Render ──────────────────────────────────────────────────────── */

  const detailedTabs = [
    { key: 'timeline', label: 'Evidence Timeline' },
    { key: 'reasoning', label: 'Reasoning Map' },
    { key: 'activation', label: 'Activation Graph' },
  ];

  return (
    <div className="build-graph-page">
      <BuildGraphMenu graphId={graphId} />

      <div className="explain-page-header">
        <h2>Explainability</h2>
        <p className="explain-page-subtitle">
          Audit how a decision was reached, trace each claim back to the source document,
          and stress-test the result with what-if scenarios.
        </p>
      </div>

      {/* Controls */}
      <div className="explain-page-controls">
        <div className="explain-control-group">
          <label>Evaluation</label>
          <select
            value={selectedInsightId}
            onChange={(e) => setSelectedInsightId(e.target.value)}
            className="explain-select"
          >
            <option value="">Select an evaluation…</option>
            {insights.map((ins) => (
              <option key={ins.insight_id} value={ins.insight_id}>
                {(ins.query || '').substring(0, 70)}
                {(ins.query || '').length > 70 ? '…' : ''}
                {' '}({new Date(ins.created_at).toLocaleDateString()})
              </option>
            ))}
          </select>
          <div className="explain-list-actions">
            <button
              type="button"
              className="explain-load-more-btn"
              onClick={handleRefresh}
              disabled={loading}
              title="Reload the evaluation list"
            >
              ↻ Refresh
            </button>
            {insightsTotal > insights.length && (
              <button
                type="button"
                className="explain-load-more-btn"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? 'Loading…' : `Load more (${insightsTotal - insights.length} remaining)`}
              </button>
            )}
          </div>
        </div>
        <div className="explain-control-group">
          <label>Domain</label>
          <select
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="explain-select explain-select--sm"
          >
            {availableDomains.map((d) => (
              <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className="explain-control-group explain-control-group--right">
          {selectedInsightId && <IntegrityBadge traceId={selectedInsightId} />}
        </div>
      </div>

      {error && <div className="explain-page-error">{error}</div>}

      {!selectedInsightId && !loading && (
        <div className="explain-page-empty">
          Select an evaluation above to view its full explainability analysis.
          If the list is empty, run a question against this graph from the Test Graph page first.
        </div>
      )}

      {selectedInsightId && dataLoading && (
        <div className="explain-page-loading">Loading explanation…</div>
      )}

      {selectedInsightId && !dataLoading && explanation && (
        <>
          {/* Question */}
          {selectedInsight?.query && (
            <div className="explain-question-box">
              <span className="explain-question-label">Question</span>
              <p className="explain-question-text">{selectedInsight.query}</p>
            </div>
          )}

          {/* Summary tiles */}
          <div className="explain-page-summary">
            <div className="explain-summary-item">
              <span className="explain-summary-label">Decision</span>
              <span className="explain-summary-value">{explanation.decision || '—'}</span>
            </div>
            <div className="explain-summary-item">
              <span className="explain-summary-label">Confidence</span>
              <span className="explain-summary-value">{explanation.confidence || '—'}</span>
            </div>
            <div className="explain-summary-item">
              <span className="explain-summary-label">Sources cited</span>
              <span className="explain-summary-value">{citationIndex.length}</span>
            </div>
            <div className="explain-summary-item">
              <span className="explain-summary-label">Decision rules</span>
              <span className="explain-summary-value">{decisionRules.length}</span>
            </div>
          </div>

          {/* Answer + Citations */}
          <div className="explain-answer-grid">
            <div className="explain-answer-main">
              <div className="explain-answer-header">
                <h3 className="explain-section-title">Answer</h3>
                <div className="explain-answer-toggle" role="tablist" aria-label="Answer view">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={answerView === 'citations'}
                    className={
                      'explain-answer-toggle-btn' +
                      (answerView === 'citations' ? ' explain-answer-toggle-btn--active' : '')
                    }
                    onClick={() => setAnswerView('citations')}
                  >
                    Source citations
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={answerView === 'plain'}
                    className={
                      'explain-answer-toggle-btn' +
                      (answerView === 'plain' ? ' explain-answer-toggle-btn--active' : '')
                    }
                    onClick={() => setAnswerView('plain')}
                  >
                    Plain English
                  </button>
                </div>
              </div>
              <div className="explain-answer-body" ref={answerRef}>
                {renderAnswer()}
              </div>
            </div>

            <aside className="explain-answer-aside">
              <CitationPanel
                citations={citationIndex}
                highlightedCite={highlightedCite}
                onCiteClick={handleCiteClick}
              />
            </aside>
          </div>

          {/* Robustness */}
          <StabilityCard queryRunId={queryRunId} domain={domain} />

          {/* Decision rules — never empty thanks to derived-rule fallback */}
          <section className="explain-section">
            <h3 className="explain-section-title">Decision Rules</h3>
            {decisionRules.length === 0 ? (
              <p className="explain-empty-note">
                No decision rules were associated with this answer.
              </p>
            ) : (
              <div className="explain-rules-list">
                {decisionRules.map((rule, idx) => (
                  <DerivedRuleCard
                    key={`${rule.rule_name}-${idx}`}
                    rule={rule}
                    onCiteClick={handleCiteClick}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Evidence factors with used / considered chips */}
          <section className="explain-section">
            <h3 className="explain-section-title">Evidence Factors</h3>
            {evidenceFactors.length === 0 ? (
              <p className="explain-empty-note">No evidence factors recorded.</p>
            ) : (
              <div className="explain-factors-list">
                {usedFactors.map((f, i) => (
                  <FactorRow
                    key={`u-${i}`}
                    factor={f}
                    onCiteClick={handleCiteClick}
                  />
                ))}
                {consideredFactors.length > 0 && (
                  <>
                    <div className="explain-factors-divider">
                      Considered, not used in answer ({consideredFactors.length})
                    </div>
                    {consideredFactors.slice(0, 20).map((f, i) => (
                      <FactorRow
                        key={`c-${i}`}
                        factor={f}
                        onCiteClick={handleCiteClick}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </section>

          {/* What-If */}
          <WhatIfPanel
            queryRunId={queryRunId}
            domain={domain}
            evidenceFactors={evidenceFactors}
            decisionRules={decisionRules}
          />

          {/* Detailed reasoning (collapsed) */}
          <section className="explain-section">
            <button
              type="button"
              className="explain-collapse-toggle"
              onClick={() => {
                setShowDetailed((v) => !v);
                if (!showDetailed && activeTab === 'reasoning') loadContributions();
              }}
              aria-expanded={showDetailed}
            >
              <span className="explain-collapse-chevron">{showDetailed ? '▾' : '▸'}</span>
              Detailed reasoning (timeline + reasoning map)
            </button>
            {showDetailed && (
              <div className="explain-detailed-body">
                <div className="explain-page-tabs">
                  {detailedTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={
                        'explain-page-tab' +
                        (activeTab === tab.key ? ' explain-page-tab--active' : '')
                      }
                      onClick={() => handleTabChange(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="explain-page-tab-content">
                  {activeTab === 'timeline' && (
                    <EvidenceTimeline
                      timeline={explanation.evaluation_timeline || []}
                      evidenceFactors={evidenceFactors}
                      excludedItems={explanation.excluded_information || []}
                      decisionRules={decisionRules}
                      executionId={selectedInsightId}
                      insightId={selectedInsightId}
                      domain={domain}
                    />
                  )}
                  {activeTab === 'reasoning' && (
                    <ReasoningMap
                      sourceDocuments={explanation.source_documents || []}
                      evidenceFactors={evidenceFactors}
                      evidenceChain={explanation.evidence_chain || []}
                      decisionRules={decisionRules}
                      contributingFactors={contributions?.contributions || explanation.contributing_factors || []}
                      decision={explanation.decision || ''}
                      confidence={explanation.confidence || ''}
                      executionId={selectedInsightId}
                      insightId={selectedInsightId}
                      domain={domain}
                    />
                  )}
                  {activeTab === 'activation' && (
                    activationTrace ? (
                      <ActivationGraph
                        seedNodes={(activationTrace.rate_coding || [])
                          .filter(n => !n.is_propagated)
                          .map(n => ({
                            node_id: n.node_id,
                            node_name: n.node_name || n.label || '',
                            similarity: n.rate_hz || n.similarity || 0.8,
                          }))}
                        firedNodes={(activationTrace.node_activations || []).map(n => ({
                          node_id: n.node_id,
                          node_name: n.node_name || n.label || '',
                          node_type: n.node_type || 'concept',
                          potency: n.potency || 0.5,
                          spike_count: n.spike_count || n.spikes || 1,
                        }))}
                        synapses={(activationTrace.synapses || []).map(s => ({
                          source_id: s.source_id,
                          target_id: s.target_id,
                          weight: s.weight || 0.5,
                          relevance: s.relevance || s.semantic_relevance || 0.3,
                          relation: s.relation || s.edge_name || '',
                        }))}
                      />
                    ) : (
                      <div className="explain-empty">Loading activation data...</div>
                    )
                  )}
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const FactorRow = ({ factor, onCiteClick }) => {
  const pages = (factor.source_page_numbers || []).filter((p) => p);
  const handleCite = (e) => {
    e.preventDefault();
    if (factor.cite_num != null) onCiteClick(factor.cite_num);
  };
  return (
    <div
      className={
        'explain-factor-row' +
        (factor.used_in_answer ? ' explain-factor-row--used' : ' explain-factor-row--considered')
      }
    >
      <div className="explain-factor-row-header">
        <span className="explain-factor-name">{factor.name}</span>
        <div className="explain-factor-badges">
          {factor.used_in_answer ? (
            <span className="explain-factor-chip explain-factor-chip--used">
              Used in answer
              {factor.cite_num != null && (
                <button
                  type="button"
                  className="explain-factor-chip-cite"
                  onClick={handleCite}
                >
                  [{factor.cite_num}]
                </button>
              )}
            </span>
          ) : (
            <span className="explain-factor-chip explain-factor-chip--considered">
              Considered, not used
            </span>
          )}
          {factor.evidence_strength && (
            <span className={`explain-factor-strength explain-factor-strength--${factor.evidence_strength.toLowerCase()}`}>
              {factor.evidence_strength}
            </span>
          )}
        </div>
      </div>
      {(factor.source_document || factor.source_section || pages.length > 0) && (
        <div className="explain-factor-source">
          {factor.source_document && <span>{factor.source_document}</span>}
          {factor.source_section && <span> · {factor.source_section}</span>}
          {pages.length > 0 && <span> · p.{pages.join(', ')}</span>}
        </div>
      )}
      {factor.verbatim_quote && (
        <blockquote className="explain-factor-quote">{factor.verbatim_quote}</blockquote>
      )}
    </div>
  );
};

export default ExplainabilityPage;
